import dynamic from 'next/dynamic';
import { ImageModalFooter } from '@components/modals/imageSelectModal/SharedComponents';
import { Form, Formik } from 'formik';
import { FormikWrapped } from '@components/form/FormikWrapped';
import { Button } from '@skipper/ui';
import React from 'react';
import {
    FileEntry,
    useCreateFileMutation,
    useFileEntriesQuery,
    useUpdateFileMutation,
} from '@graphql/generated-graphql';
import { getClient } from '@graphql/graphql-client/graphClient';
import { SimpleSpinner } from '@components/common/loading/SimpleSpinner';
import { useUniversalModalDispatch } from '@components/context/UniversalModalContext';
import { useDispatchError } from '@hooks/error/useDispatchError';

const DynamicEditor: any = dynamic<any>(
    () => import('@components/editor/SkipperEditor').then((mod) => mod.SkipperEditor),
    { ssr: false },
);

export function SkipperPaperEditor(props: {
    createFile?: ReturnType<typeof useCreateFileMutation>;
    authorizer?: any;
    fileEntryId?: string;
    onSave?: (fileEntry: FileEntry) => void;
    embedded?: boolean;
}) {
    const dispatch = useDispatchError();
    const { toggle } = useUniversalModalDispatch();
    const updateFile = useUpdateFileMutation(getClient('files'), {
        onSettled: (data) => {
            if (data?.updateFile?.success) {
                toggle(false);
                fileEntries.remove();
                if (props.onSave) props.onSave(data.updateFile.fileEntry as any);
            } else dispatch('Could not save your Skipper Paper');
        },
    });
    const createFile = useCreateFileMutation(getClient('files'), {
        onSettled: (data) => {
            if (data?.createFile?.success) {
                toggle(false);
                fileEntries.remove();
                if (props.onSave) props.onSave(data.createFile.fileEntry as any);
            } else dispatch('Could not create Skipper Paper');
        },
    });
    const fileEntries = useFileEntriesQuery(
        getClient('files'),
        {
            ids: [props.fileEntryId],
        },
        { keepPreviousData: false, enabled: !!props.fileEntryId },
    );
    const fileEntry = fileEntries.data?.fileEntries?.[0];

    const handleSubmit = async (values: { title: string; content: string }) => {
        if (props.fileEntryId)
            updateFile.mutate({
                id: props.fileEntryId,
                title: values.title,
                skipperPaperContent: values.content || '',
            });
        else
            (props.createFile || createFile).mutate({
                title: values.title,
                authorizerItem: props.authorizer,
                skipperPaperContent: values.content || '',
            });
    };

    const saving = updateFile.isLoading || (props.createFile || createFile).isLoading;

    return (
        <div className={props.embedded ? 'h-full' : 'fixed inset-0 z-9999'}>
            <div
                className={
                    props.embedded ? 'h-full' : 'absolute inset-0 flex items-center justify-center'
                }
            >
                <div
                    className={
                        props.embedded
                            ? 'h-full'
                            : 'relative object-contain bg-white rounded-md overflow-hidden max-w-[980px] w-full h-5/6'
                    }
                    style={{ maxHeight: '700px' }}
                >
                    <div className="relative flex flex-col flex-1 overflow-hidden items-center justify-center h-full w-full">
                        {!fileEntries.isLoading ? (
                            <div className="overflow-hidden grow w-full">
                                <Formik
                                    initialValues={{
                                        title: fileEntry?.title,
                                        content: fileEntry?.skipperPaperContent,
                                    }}
                                    onSubmit={handleSubmit}
                                >
                                    {({ values, setFieldValue }) => (
                                        <Form className={`flex flex-col gap-2 h-full w-full`}>
                                            <div className={`px-2 pt-2`}>
                                                <FormikWrapped
                                                    labelText=""
                                                    id="title"
                                                    placeholder={'Document Title'}
                                                    variant="small"
                                                    required={true}
                                                />
                                            </div>
                                            <div className={`overflow-hidden grow`}>
                                                <DynamicEditor
                                                    // value={''}
                                                    initialData={values.content}
                                                    variant="skipper-paper"
                                                    onChange={(value: string) => {
                                                        setFieldValue('content', value);
                                                    }}
                                                />
                                            </div>
                                            <ImageModalFooter>
                                                <div className="flex items-center justify-end pr-3 gap-x-3 bg-white h-full py-3">
                                                    {props.fileEntryId && (
                                                        <Button
                                                            size="large"
                                                            type="button"
                                                            variant="light"
                                                            onClick={() => toggle(false)}
                                                            disabled={saving}
                                                        >
                                                            Cancel
                                                        </Button>
                                                    )}
                                                    <Button
                                                        size="large"
                                                        type="submit"
                                                        disabled={saving}
                                                    >
                                                        {props.fileEntryId
                                                            ? 'Save & Close'
                                                            : 'Save'}
                                                    </Button>
                                                </div>
                                            </ImageModalFooter>
                                        </Form>
                                    )}
                                </Formik>
                            </div>
                        ) : (
                            <SimpleSpinner />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
